import React, { useEffect, useState } from 'react';
import { AppProvider, useApp } from './context/AppContext';
import { hasPermission } from './utils/roleHelpers';
import apiService from './services/ApiService';
import LoginPage from './components/auth/LoginPage';
import Sidebar from './components/layout/Sidebar';
import Header from './components/layout/Header';
import Dashboard from './components/dashboard/Dashboard';
import InventoryPage from './components/inventory/InventoryPage';
import FinancialPage from './components/financial/FinancialPage';
import ToolsPage from './components/tools/ToolsPage';
import SettingsPage from './components/settings/SettingsPage';
import ShippingPage from './components/shipping/ShippingPage';
import LandedCostPage from './components/pricing/LandedCostPage';

function AppContent() {
  const { currentUser, login, currentPage, sidebarOpen, setSidebar } = useApp();
  const [authChecking, setAuthChecking] = useState(true);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const initial = window.innerWidth < 768;
    if (initial) setSidebar(false);
    const handler = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setSidebar(!mobile);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [setSidebar]);


  // Verify token on load
  useEffect(() => {
    async function checkAuth() {
      const token = localStorage.getItem('premier_token');
      if (!token) { setAuthChecking(false); return; }
      try {
        const data = await apiService.verifyToken();
        login(data.user);
      } catch {
        apiService.clearAuthToken();
      } finally {
        setAuthChecking(false);
      }
    }
    checkAuth();
  }, [login]);

  function handleLogin(user, token) {
    apiService.setAuthToken(token);
    login(user);
  }

  if (authChecking) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0f172a' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <img src="/logo-white.png" alt="Premier" style={{ height: 40, width: 'auto', opacity: 0.9 }} />
          <div style={{ color: '#475569', fontSize: 13, fontWeight: 500 }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <LoginPage onLogin={handleLogin} />;
  }

  function renderPage() {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard />;
      case 'inventory':
        if (!hasPermission(currentUser, 'inventory')) return <AccessDenied />;
        return <InventoryPage />;
      case 'financial':
        if (!hasPermission(currentUser, 'financial')) return <AccessDenied />;
        return <FinancialPage />;
      case 'tools':
        if (!hasPermission(currentUser, 'tools')) return <AccessDenied />;
        return <ToolsPage />;
      case 'settings':
        if (!hasPermission(currentUser, 'settings')) return <AccessDenied />;
        return <SettingsPage />;
      case 'shipping':
        if (!hasPermission(currentUser, 'shipping')) return <AccessDenied />;
        return <ShippingPage />;
      case 'pricing':
        if (!hasPermission(currentUser, 'financial')) return <AccessDenied />;
        return <LandedCostPage />;
      default:
        return <Dashboard />;
    }
  }

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: '#f1f5f9' }}>
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebar(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99, backdropFilter: 'blur(1px)' }}
        />
      )}
      <Sidebar isMobile={isMobile} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        <Header />
        <main style={{ flex: 1, overflow: 'hidden' }}>
          {renderPage()}
        </main>
      </div>
    </div>
  );
}

function AccessDenied() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <div style={{ background: 'white', borderRadius: 12, padding: '32px 40px', textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 8 }}>Access Denied</h2>
        <p style={{ color: '#6b7280', fontSize: 14 }}>You don't have permission to view this page.</p>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}
